import {
  useGenerateReceiptBatchPreview,
  useGetReceiptBatchListPhoenix,
} from '@pflegenavi/frontend/api-nursing-home';
import {
  useNursingHomeContext,
  useSelectedEmployeeIds,
} from '@pflegenavi/frontend/nursing-home-context';
import type { UrlTypeNameMapping } from '@pflegenavi/web-components';
import { useErrorSnackbar, useUrlTableState } from '@pflegenavi/web-components';
import { keepPreviousData } from '@tanstack/react-query';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ReceiptBatchOverviewTableView } from './ReceiptBatchOverviewTableView';
import { ReceiptBatchTableEnlargeGallery } from './ReceiptBatchTableEnlargeGallery';
import { useReceiptBatchFilters } from './useReceiptBatchFilters';

interface ReceiptBatchOverviewTableProps {
  serviceProviderIds?: string[];
  recurringItemId?: string;
}

export const ReceiptBatchOverviewTable: FC<ReceiptBatchOverviewTableProps> = ({
  serviceProviderIds,
  recurringItemId,
}) => {
  const { selectedNursingHome } = useNursingHomeContext();
  const employeeIds = useSelectedEmployeeIds();
  const filtersState = useReceiptBatchFilters();
  const isServiceProviderPinned = serviceProviderIds !== undefined;
  const { page, pageSize, setPage, setPageSize } = useUrlTableState({
    defaultPageSize: 10,
  });

  // Filters reset the page themselves. The institution and employee selection
  // live outside the URL, so a switch there resets it here. Compared against
  // the previous value so a deep link to page 3 is not reset on mount.
  const scopeKey = `${selectedNursingHome?.id}:${employeeIds?.join(',')}`;
  const previousScopeKey = useRef(scopeKey);
  useEffect(() => {
    if (previousScopeKey.current !== scopeKey) {
      previousScopeKey.current = scopeKey;
      setPage(0);
    }
  }, [scopeKey, setPage]);

  const { data, isLoading } = useGetReceiptBatchListPhoenix(
    {
      params: {
        ...filtersState.filters,
        // The pinned view hides these pills, so a stale URL value must not
        // filter rows without a way to clear it.
        ...(isServiceProviderPinned && {
          service_provider_name: undefined,
          receipt_type_ids: undefined,
        }),
        page: page + 1, // Flop is 1-indexed, MUI TablePagination is 0-indexed
        page_size: pageSize,
        nursing_home_ids: selectedNursingHome?.id
          ? [selectedNursingHome.id]
          : [],
        created_by_employee_ids: employeeIds,
        order_by: ['updated_on'],
        order_directions: ['desc'],
        service_provider_ids: serviceProviderIds,
        recurring_item_ids: recurringItemId ? [recurringItemId] : undefined,
      },
    },
    {
      placeholderData: keepPreviousData,
      enabled: selectedNursingHome?.id !== undefined,
    }
  );

  const [receiptImageIds, setReceiptImageIds] = useState<string[] | undefined>(
    undefined
  );
  const [imageGalleryTitle, setImageGalleryTitle] = useState<
    string | undefined
  >(undefined);

  const [openImageGallery, setOpenImageGallery] = useState(false);

  const { mutateAsync: generateReceiptBatch } = useGenerateReceiptBatchPreview(
    useErrorSnackbar().enqueueErrorSnackbar
  );

  const [generatedReceiptImageUrls, setGeneratedReceiptImageUrls] = useState<
    string[]
  >([]);
  const [generatedReceiptTypeMap, setGeneratedReceiptTypeMap] =
    useState<UrlTypeNameMapping>(new Map());

  const generateReceiptBatchPreview = useCallback(
    async (batchId: string, filename: string) => {
      try {
        const receiptBlob = await generateReceiptBatch({
          batchId,
        });
        if (receiptBlob) {
          const file = window.URL.createObjectURL(receiptBlob);

          setGeneratedReceiptImageUrls([file]);
          setGeneratedReceiptTypeMap(
            new Map([
              [
                file,
                {
                  type: 'application/pdf',
                  data: Promise.resolve(receiptBlob),
                  name: filename,
                },
              ],
            ])
          );
          setOpenImageGallery(true);
        }
      } catch {
        // handleFailedPreviewGenerationError(e);
      }
    },
    [generateReceiptBatch]
  );

  const onClickReceiptIcon = useCallback(
    ({
      id,
      receiptImageIds,
      title,
      generateReceipts,
    }: {
      id: string;
      receiptImageIds: string[] | undefined;
      title: string;
      generateReceipts: boolean;
    }) => {
      if (generateReceipts) {
        generateReceiptBatchPreview(id, title); // Title contains the filename when in preview mode
        return;
      }
      setReceiptImageIds(receiptImageIds);
      setImageGalleryTitle(title);
      if (receiptImageIds && receiptImageIds.length > 0) {
        setOpenImageGallery(true);
      }
    },
    [generateReceiptBatchPreview]
  );

  return (
    <>
      <ReceiptBatchOverviewTableView
        data={data?.data ?? []}
        totalRowCount={data?.meta?.total_count ?? 0}
        page={page}
        pageSize={pageSize}
        onChangePage={setPage}
        onChangePageSize={setPageSize}
        isLoading={isLoading}
        onClickReceiptIcon={onClickReceiptIcon}
        filtersState={filtersState}
        isServiceProviderPinned={isServiceProviderPinned}
      />
      <ReceiptBatchTableEnlargeGallery
        openImageGallery={openImageGallery}
        setOpenImageGallery={setOpenImageGallery}
        batchImageIds={receiptImageIds}
        title={imageGalleryTitle ?? ''}
        generatedReceiptImageUrls={generatedReceiptImageUrls}
        setGeneratedReceiptImageUrls={setGeneratedReceiptImageUrls}
        generatedReceiptTypeMap={generatedReceiptTypeMap}
      />
    </>
  );
};
